/**
 * Event-driven progress UI for the v2 agent pipeline.
 *
 * Two responsibilities:
 *  (1) Render the pipeline's per-frame step log — one row per backend
 *      event (plan_proposed, plan_step_started/completed,
 *      synth_started/completed/delta, ask_user, error). The critic
 *      was removed June 2026; synth_delta now drives a live typewriter
 *      render into content.parts[0] instead of being buffered.
 *      Policy (user preference 2026-04-24): "more comm to user, the
 *      merrier" — do NOT collapse started/completed pairs.
 *
 *  (2) Render an inline prompt when the pipeline pauses on ask_user /
 *      plan_approval. For plan_approval this is a simple approve/cancel
 *      button row. For ask_user it is a FORM — one control per
 *      clarification (multi_choice → checkboxes, single_choice →
 *      radios, number → number input, text → text input, format_preference
 *      → radios). A single Submit button POSTs all answers as one
 *      plan_decision. Individual option clicks must NOT trigger submit —
 *      the prior bug was: clicking one checkbox immediately submitted a
 *      half-answer, aborting the upstream stream and breaking the UI.
 */
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight
} from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { OSLoader } from "~/components/base-loader"
import MarkdownRender from "~/components/markdown-render"
import { useGetAccounts } from "~/hooks/accounts"

export interface IAgentStepEvent {
  id: number
  eventType: string
  title: string
  status: "running" | "done" | "failed"
  detail?: string
  stepId?: string
  raw?: any
}

export interface IClarification {
  key: string
  question: string
  context?: string
  answer_type:
    | "text"
    | "single_choice"
    | "multi_choice"
    | "number"
    | "format_preference"
  options?: string[]
  default?: string | null
  /**
   * Pre-checked values for `multi_choice` clarifications. Used by
   * in-plan step-confirmation gates: BE returns the DB-grounded list
   * here, FE seeds the form with all of them checked, and the AE
   * deselects noise. Empty / undefined → form starts unchecked
   * (the legacy behaviour for planner_clarification multi-choices).
   */
  default_values?: string[]
  /**
   * When set, the FE sources the option list from a local data source
   * instead of the (typically empty) `options` array on this payload.
   * Defined values:
   *   - "user_authorized_accounts" → useGetAccounts() (the same data
   *     powering the global account-selection dropdowns).
   * Backend sets this for account-picker clarifications so 20k-account
   * tenants don't have to ship the full list over SSE every turn.
   */
  options_source?: string
}

export interface IAskUserOption {
  value: string
  label: string
}

export interface IAskUser {
  /** planner_clarification | plan_approval | execution_ask_user | execution_failure */
  source: string
  prompt: string
  plan?: any
  /** Legacy single-choice prompt (approve/cancel). Mutually exclusive with `clarifications`. */
  options?: IAskUserOption[]
  /** Structured clarifications — one form control each, submitted together. */
  clarifications?: IClarification[]
}

/** Shape of decision payload the parent will POST to /v2/.../resume. */
export interface IResumeDecision {
  action: string
  /**
   * For ask_user with one clarification: the string value directly.
   * For ask_user with >1 clarifications: a map {key: string|string[]}.
   * For plan_approval: omitted.
   */
  answer?: string | Record<string, string | string[]>
  /**
   * Per-question recap the FE built at submit time ([{question, answer}],
   * empty answer = skipped). Used live by the resume handler to render the
   * optimistic "Your answers" card immediately, client-side. NOT persisted as
   * its own event — on reload the card is rebuilt from the persisted decision
   * turn + the prior ask_user's clarifications (see v2-history-replay.ts).
   */
  answers_recap?: { question: string; answer: string }[]
}

export default function AgentStepsUI({
  steps,
  askUser,
  isPending,
  isFinalAnswer,
  onResume,
  forceDefault
}: {
  steps: IAgentStepEvent[]
  askUser?: IAskUser | null
  /**
   * The assistant message's pending flag. Drives a "still working"
   * loader row below the steps list for windows where the backend is
   * processing but hasn't emitted a new event yet — crucial for the
   * post-resume gap between Submit and the first plan_step_started
   * (can be 5-20s on Opus planner calls). Without this the UI goes
   * silent and the user thinks the app froze.
   */
  isPending?: boolean
  /**
   * True only when the worker has emitted its terminal `done` (with
   * status=done) — i.e., the bubble has populated `content`, no error,
   * no pending askUser. This is the SINGLE signal that gates auto-
   * collapse (ENG-1316): clarifications, errors, plan_approval, and
   * any other mid-flight pause leave this `false`, so the panel stays
   * open and the user can see context for the action they need to
   * take.
   */
  isFinalAnswer?: boolean
  onResume?: (decision: IResumeDecision) => void
  /** When true, a PMB v2 clarification picker auto-answers with
   *  mode="default" on arrival instead of rendering the Q1/Q3 form —
   *  set by the agent-search "Pre-meeting brief" tool card. */
  forceDefault?: boolean
}) {
  const { t } = useTranslation()
  // "Still working" — show the loader footer when the message is
  // pending, there's no open ask_user prompt (the user is not the
  // blocker here), and no existing step is currently in `running` state
  // (if a step is running, its own spinner is the indicator; avoid
  // double loaders).
  const hasRunningStep = steps.some((s) => s.status === "running")
  const showWorkingFooter = Boolean(isPending) && !askUser && !hasRunningStep
  const isLive = Boolean(isPending) || hasRunningStep

  // Default expanded only while the pipeline is actively running. History
  // / reload (isPending=false, no running step) mounts collapsed — user
  // toggles to inspect. useState initializer captures mount-time state so
  // the user's manual toggle survives subsequent re-renders.
  // Declared before the early return below to keep hook order stable.
  const [expanded, setExpanded] = useState(() => isLive)

  // Auto-collapse ONLY when the worker emits its terminal `done` —
  // signalled by `isFinalAnswer` (parent sets this when content has
  // landed, no error, no pending askUser). Every mid-flight pause
  // state (plan_approval, planner_clarification, execution_ask_user,
  // execution_failure, error) leaves `isFinalAnswer` false, so the
  // panel stays open and the user can see context for the action
  // they need to take (ENG-1316). Without this gate the panel would
  // collapse on every transition where `isLive` flips false — which
  // includes all those pause states because both `isPending` flips
  // false and no step is `running` while the worker waits.
  const prevLiveRef = useRef(isLive)
  useEffect(() => {
    if (prevLiveRef.current && !isLive && isFinalAnswer) {
      setExpanded(false)
    }
    prevLiveRef.current = isLive
  }, [isLive, isFinalAnswer])

  if (!steps.length && !askUser) return null

  const headerLabel = isLive
    ? t("os_agents.label_researching", { defaultValue: "Researching" })
    : t("os_agents.label_researched_count", {
        count: steps.length,
        defaultValue: "Researched · {{count}} steps"
      })

  return (
    <div className="flex flex-col gap-3 w-full">
      {steps.length > 0 && (
        <div
          className="py-3 flex flex-col gap-2.5"
          data-testid="agent-steps-panel"
        >
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="flex items-center gap-1.5 text-13 font-medium text-gray-500 dark:text-dark-6/70 hover:opacity-80 transition-opacity"
            aria-expanded={expanded}
            data-testid="agent-steps-toggle"
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span>{headerLabel}</span>
          </button>
          {expanded &&
            steps.map((s) => (
              <div
                key={s.id}
                className="flex flex-col gap-1"
                data-testid={`agent-step-${s.id}`}
              >
                <div
                  className="flex gap-2 items-start"
                  data-testid="agent-step-row"
                >
                  <div className="shrink-0 mt-0.5">
                    {s.status === "done" ? (
                      <CheckCircle size={16} color="#10b981" />
                    ) : s.status === "failed" ? (
                      <AlertCircle size={16} color="#ef4444" />
                    ) : (
                      <OSLoader className="xs" />
                    )}
                  </div>
                  <span className="text-13 text-gray-500 dark:text-dark-6/70">
                    {s.title}
                  </span>
                </div>
                {s.detail && (
                  // plan_proposed needs its full multi-line detail visible
                  // (rationale + numbered step titles) so the user can see
                  // WHAT will run before approving. Step output details
                  // (plan_step_completed / synth_completed) can be huge and
                  // are kept clamped so the step list stays scannable.
                  //
                  // MarkdownRender handles `**bold**`, `> blockquote`,
                  // bullet lists, tables, links, etc. — the BE freely
                  // emits markdown in step text/details (fallback notes,
                  // priorities tables, brief summaries) and previously
                  // those leaked as literal `**Note:**` asterisks here.
                  // Single fix at the render site covers every current
                  // and future markdown leak without per-string edits in
                  // the BE (which would be whack-a-mole).
                  <div
                    className={`pl-6 ${
                      s.eventType === "plan_proposed" ? "" : "line-clamp-3"
                    }`}
                  >
                    <MarkdownRender
                      content={s.detail}
                      // `markdown-content` activates the global compound
                      // selector `.markdown.markdown-content { h1-h6 {
                      // font-size: clamp(11px, 0.9vw, 13px) } }` in
                      // tailwind.css, collapsing heading sizes to chip-scale.
                      // Per-element [&_h1] Tailwind utilities lose against
                      // the global `.markdown h1 { font-size: 20-24px }`
                      // rule (equal specificity, global wins via source
                      // order) — `markdown-content` is the codebase's
                      // built-in escape hatch for this exact case.
                      // The remaining Tailwind bits tighten vertical margins
                      // and list indent so the chip stays compact.
                      className="markdown-content !p-0 text-[11px] text-gray-500 dark:text-dark-6/70 [&_p]:!my-0 [&_p]:!text-[11px] [&_p]:!text-gray-500 [&_p]:dark:!text-dark-6/70 [&_p]:whitespace-pre-wrap [&_h1]:!my-0 [&_h2]:!my-0 [&_h3]:!my-0 [&_h4]:!my-0 [&_h5]:!my-0 [&_h6]:!my-0 [&_ul]:!pl-4 [&_ul]:!my-0 [&_ol]:!pl-4 [&_ol]:!my-0 [&_li]:!my-0 [&_li]:!text-[11px] [&_li]:!text-gray-500 [&_li]:dark:!text-dark-6/70 [&_table]:!text-[11px] [&_blockquote]:border-l-2 [&_blockquote]:pl-2 [&_blockquote]:!my-0 [&_blockquote]:!text-gray-500 [&_blockquote]:italic [&_code]:text-[10px]"
                    />
                  </div>
                )}
                {s.eventType === "your_answers" &&
                  Array.isArray(s.raw?.answers) && (
                    <dl className="ml-6 rounded-lg border border-light-border/60 dark:border-border/60 bg-genesis dark:bg-dark-3 p-2.5 flex flex-col">
                      {(
                        s.raw.answers as {
                          question: string
                          answer: string
                        }[]
                      ).map((r, i) => (
                        <div
                          key={i}
                          className="flex items-start justify-between gap-4 py-1.5 border-b border-light-border/40 dark:border-border/40 last:border-b-0"
                        >
                          <dt className="text-13 text-dark-2/70 dark:text-dark-6/70">
                            {r.question}
                          </dt>
                          <dd
                            className={
                              "text-13 text-right " +
                              (r.answer
                                ? "font-normal text-dark-2/80 dark:text-dark-6/80"
                                : "italic text-dark-2/40 dark:text-dark-6/40")
                            }
                          >
                            {r.answer || t("os_agents.label_skipped")}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}
              </div>
            ))}
          {expanded && showWorkingFooter && (
            <div
              className="flex gap-2 items-center pt-1"
              data-testid="agent-steps-working"
            >
              <OSLoader className="xs" />
              <span className="text-13 text-gray-500 dark:text-dark-6/70">
                {t("os_agents.label_still_working", {
                  defaultValue: "Still working"
                })}
              </span>
            </div>
          )}
        </div>
      )}

      {askUser && onResume && (
        // Key on the question identity (source + prompt) so a NEW ask_user
        // arriving on the same assistant message forces React to remount
        // AskUserCard with fresh state. Without this, `submitted` /
        // `mode` / `feedback` / `answers` carry over from the previous
        // question — e.g. after Submitting Q1/Q2/Q3 the planner emits a
        // plan_approval ask_user; the SAME AskUserCard instance re-renders
        // with `submitted=true` from the previous click, disabling all
        // Approve/Modify/Cancel buttons. Restarting the session unmounted
        // the message → state reset → buttons worked. The key sidesteps
        // that whole class of stale-state bugs.
        <AskUserCard
          key={`${askUser.source ?? ""}|${askUser.prompt ?? ""}`}
          askUser={askUser}
          onResume={onResume}
          forceDefault={forceDefault}
        />
      )}
    </div>
  )
}

/**
 * Interactive card for an ask_user / plan_approval prompt.
 *
 * Branches on the payload shape:
 *  - plan_approval (top-level options, no clarifications) → one-click buttons.
 *  - ask_user with clarifications[] → form with per-clarification controls
 *    and one Submit button. Collects all answers locally before calling
 *    onResume once.
 */

// Matches a LinkedIn member profile URL anywhere in the string (scheme and
// www./in./country-code subdomain optional) — mirrors the backend's
// extract_linkedin_url regex. Used to validate the person-disambiguation
// "None of these" input, which must be an exact profile URL: the name search
// already failed, so a company/name there would just re-run a fuzzy lookup.
const LINKEDIN_PROFILE_URL_RE =
  /(?:https?:\/\/)?(?:www\.|in\.|[a-z]{2}\.)?linkedin\.com\/in\/[A-Za-z0-9\-_%.]+\/?/i
function isLinkedInProfileUrl(s: string): boolean {
  return LINKEDIN_PROFILE_URL_RE.test((s || "").trim())
}

function AskUserCard({
  askUser,
  onResume,
  forceDefault
}: {
  askUser: IAskUser
  onResume: (decision: IResumeDecision) => void
  forceDefault?: boolean
}) {
  const { t } = useTranslation()
  const clarifications = askUser.clarifications || []
  const isApproval =
    clarifications.length === 0 && (askUser.options || []).length > 0
  // Free-text path: agent invoked ask_user_tool with a single prompt and
  // no structured clarifications (e.g. "Provide the LinkedIn URL for
  // <person>"). The user needs a textarea to type their answer; without
  // this we'd render only a Submit button with nowhere to type.
  const isFreeText = clarifications.length === 0 && !isApproval
  const [freeTextAnswer, setFreeTextAnswer] = useState("")

  // Per-clarification answers: key -> string or string[].
  // Seeded with defaults so "Last 90 days" is pre-selected etc.
  // For multi_choice, BE may pre-check options via `default_values`
  // (used by in-plan step_confirmation gates: AE sees the DB-grounded
  // list already selected and deselects noise).
  const [answers, setAnswers] = useState<Record<string, string | string[]>>(
    () => {
      const seed: Record<string, string | string[]> = {}
      for (const c of clarifications) {
        if (c.answer_type === "multi_choice") {
          seed[c.key] = Array.isArray(c.default_values)
            ? [...c.default_values]
            : []
        } else if (c.default != null) {
          seed[c.key] = c.default
        }
      }
      return seed
    }
  )
  // Free-text the user types when they pick an "Other (specify)"-style
  // option. Kept separate from `answers[key]` so the chip remains
  // checked visually while the text is being entered — only merged into
  // the submission payload at submit time.
  const [otherTexts, setOtherTexts] = useState<Record<string, string>>({})

  const [submitted, setSubmitted] = useState(false)

  // Keys the user chose to Skip. Skipped questions are excluded from
  // validation (they don't block Next/Submit) and OMITTED from the
  // submitted answer map — the backend treats an absent key as
  // "not provided" (→ sourced from records / left blank), and the
  // answers recap shows it as "Skipped".
  const [skippedKeys, setSkippedKeys] = useState<Set<string>>(new Set())

  // One-question-at-a-time cursor for multi-clarification flows. The
  // backend still emits all questions in a single payload; we render
  // them sequentially so the rep can focus on one at a time (Claude-
  // Code-style). Single-clarification / free-text / approval payloads
  // bypass this entirely.
  //
  // Navigation: Next → validates the CURRENT question only and advances
  // the cursor. Submit (final question) validates the current question
  // and posts the entire {key → answer} map at once via onResume — the
  // existing answer-map shape on the resume contract is unchanged.
  const [currentIdx, setCurrentIdx] = useState(0)

  // PMB v2 "Pre-meeting brief" tool card → auto-answer the Q1/Q3 picker
  // with mode="default" the moment it arrives, so the card click goes
  // straight to the default brief without prompting. Fires once; the
  // AskUserCard is keyed on question identity upstream so a fresh
  // question gets a fresh instance (and a fresh auto-fire if applicable).
  const autoDefaultFiredRef = useRef(false)
  useEffect(() => {
    if (!forceDefault || autoDefaultFiredRef.current) return
    if (!_isPmbV2Picker(clarifications)) return
    autoDefaultFiredRef.current = true
    setSubmitted(true)
    onResume({
      action: "answer_question",
      answer: { mode: "default" } as unknown as Record<
        string,
        string | string[]
      >
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceDefault, clarifications])

  const setAnswer = (key: string, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [key]: value }))
  }

  const setOtherText = (key: string, text: string) => {
    setOtherTexts((prev) => ({ ...prev, [key]: text }))
  }

  // If ANY clarification declares options_source = "user_authorized_accounts",
  // pre-fetch the accounts list once at this level. We compute the
  // effective options map below using the result, so the
  // other-detection / resolveAnswer logic and the picker render both
  // see the same source-of-truth (avoids the parent's checks running
  // against an empty list while the child fetches its own copy).
  const needsAuthorizedAccounts = clarifications.some(
    (c) => c.options_source === "user_authorized_accounts"
  )
  const { data: accountsData } = useGetAccounts(
    undefined,
    needsAuthorizedAccounts
  )
  const liveAccountNames = useMemo(() => {
    if (!needsAuthorizedAccounts) return [] as string[]
    const list =
      (accountsData as any)?.data?.accounts ?? (accountsData as any)?.data ?? []
    return Array.isArray(list)
      ? list
          .map((a: any) => a?.name || a?.account_name)
          .filter((n: any) => typeof n === "string" && n.length > 0)
      : []
  }, [needsAuthorizedAccounts, accountsData])

  // Effective options per clarification key. For options_source-marked
  // clarifications we synthesize from local data + the canonical
  // "Other (please specify)" sentinel. For everything else we pass
  // through the LLM-emitted options unchanged.
  const effectiveOptionsByKey = useMemo(() => {
    const out: Record<string, string[]> = {}
    for (const c of clarifications) {
      if (c.options_source === "user_authorized_accounts") {
        out[c.key] = [
          ...liveAccountNames,
          t("os_agents.label_other_please_specify")
        ]
      } else {
        out[c.key] = c.options || []
      }
    }
    return out
  }, [clarifications, liveAccountNames])

  // Identify the "other/specify/custom" sentinel option for each
  // clarification (if any). Matches loose wording: "Other",
  // "Other (specify)", "Other (please type names)", "Custom range",
  // and the person-disambiguation escape "None of these - I'll give
  // more detail (...)" — selecting any of these reveals the free-text
  // input so the user can type their own value (e.g. a LinkedIn URL).
  // Computed against the EFFECTIVE options so account-picker
  // clarifications (where the LLM-emitted options is empty) still
  // detect the sentinel via the FE-appended one.
  const otherOptionByKey = useMemo(() => {
    const out: Record<string, string | undefined> = {}
    for (const c of clarifications) {
      out[c.key] = (effectiveOptionsByKey[c.key] || []).find((o) =>
        /\b(other|specify|custom)\b|none of these/i.test(o)
      )
    }
    return out
  }, [clarifications, effectiveOptionsByKey])

  const isOtherSelected = (c: IClarification) => {
    const sentinel = otherOptionByKey[c.key]
    if (!sentinel) return false
    const v = answers[c.key]
    return Array.isArray(v) ? v.includes(sentinel) : v === sentinel
  }

  // Resolve a clarification's final answer value: if the "Other" sentinel
  // is selected, replace it with the comma-split of the typed text.
  const resolveAnswer = (c: IClarification): string | string[] => {
    const raw = answers[c.key]
    const sentinel = otherOptionByKey[c.key]
    if (!sentinel || !isOtherSelected(c)) return raw
    const typed = (otherTexts[c.key] || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
    if (Array.isArray(raw)) {
      return [...raw.filter((x) => x !== sentinel), ...typed]
    }
    // single-choice with Other: the text itself IS the answer.
    return typed[0] || ""
  }

  // A clarification is treated as optional when the question text says so —
  // the planner phrasing convention is "Anything specific...? (optional)".
  // The PMB v2 free-text Q3 follows this. Skip required-ness check for those
  // so Submit isn't blocked when the rep leaves them blank.
  const isOptional = (c: IClarification): boolean => {
    return /\(\s*optional\s*\)/i.test(c.question || "")
  }

  // Validate a single clarification — returns its key if missing/invalid,
  // or null if good. Shared by the per-question Next-button disable
  // (current question only) and the per-question advance / final submit.
  const validateOne = (c: IClarification): string | null => {
    const v = answers[c.key]
    const optional = isOptional(c)
    if (c.answer_type === "multi_choice") {
      if (!optional && (!Array.isArray(v) || v.length === 0)) return c.key
    } else {
      if (!optional && (v == null || v === "")) return c.key
    }
    if (isOtherSelected(c)) {
      const typed = (otherTexts[c.key] || "").trim()
      if (!typed) return c.key
      // Person-disambiguation escape: the typed value MUST be a real LinkedIn
      // profile URL. The name search already failed to pinpoint the person, so
      // a company/name here would only re-run that same fuzzy lookup — the
      // reliable recovery is the exact profile URL.
      if (
        c.options_source === "person_disambiguation" &&
        !isLinkedInProfileUrl(typed)
      )
        return c.key
    }
    return null
  }

  // Current-question validity for the Next/Submit button. Scoping to the
  // current cursor (instead of all clarifications) is what lets a user
  // advance step-by-step without unvisited future questions blocking
  // the button — each one becomes the "current" before it's gated.
  const currentClarif = clarifications[currentIdx]
  const currentMissing = useMemo(() => {
    if (!currentClarif) return null
    if (skippedKeys.has(currentClarif.key)) return null
    return validateOne(currentClarif)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, currentClarif, otherTexts, otherOptionByKey, skippedKeys])

  // Cross-form validity (defensive — used on final submit to catch any
  // gap from back-navigation that left a previously-answered question
  // empty). Will normally be null by the time we reach the last Q since
  // each was gated through Next.
  const missingKey = useMemo(() => {
    for (const c of clarifications) {
      if (skippedKeys.has(c.key)) continue
      const miss = validateOne(c)
      if (miss) return miss
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, clarifications, otherTexts, otherOptionByKey, skippedKeys])

  const isMultiQ = clarifications.length > 1
  const isLastQ = currentIdx >= clarifications.length - 1
  const goNext = () => {
    if (currentMissing != null) return
    setCurrentIdx((i) => Math.min(i + 1, clarifications.length - 1))
  }
  const goBack = () => {
    setCurrentIdx((i) => Math.max(i - 1, 0))
  }

  // Per-question recap rows ([{question, answer}]; empty answer = skipped).
  // Shared by the live (optimistic) recap render and the durable
  // `answers_recap` we relay so the backend can echo it into the timeline.
  const buildRecap = (
    skip: Set<string>
  ): { question: string; answer: string }[] =>
    isFreeText
      ? [
          {
            question: askUser.prompt || t("os_agents.label_your_answer"),
            answer: freeTextAnswer.trim()
          }
        ]
      : clarifications.map((c) => {
          if (skip.has(c.key)) return { question: c.question, answer: "" }
          const r = resolveAnswer(c)
          return {
            question: c.question,
            answer: Array.isArray(r)
              ? r.join(", ")
              : typeof r === "string"
                ? r
                : ""
          }
        })

  // Skip the current question: mark it skipped, then advance (or submit if
  // it's the last / a single / free-text). Skipped keys are omitted from the
  // payload so the backend sources them itself or leaves them blank.
  const skipCurrent = () => {
    if (submitted) return
    if (isFreeText) {
      setSubmitted(true)
      onResume({
        action: "answer_question",
        answer: "",
        answers_recap: [
          {
            question: askUser.prompt || t("os_agents.label_your_answer"),
            answer: ""
          }
        ]
      })
      return
    }
    const key = (isMultiQ ? currentClarif?.key : clarifications[0]?.key) || ""
    if (key) setSkippedKeys((s) => new Set(s).add(key))
    if (isMultiQ && !isLastQ) {
      setCurrentIdx((i) => Math.min(i + 1, clarifications.length - 1))
    } else {
      submit(key)
    }
  }

  const submit = (extraSkipKey?: string) => {
    if (submitted) return
    setSubmitted(true)
    if (isFreeText) {
      onResume({
        action: "answer_question",
        answer: freeTextAnswer.trim(),
        answers_recap: buildRecap(new Set())
      })
      return
    }
    const skip = new Set(skippedKeys)
    if (extraSkipKey) skip.add(extraSkipKey)
    const recap = buildRecap(skip)
    const resolved: Record<string, string | string[]> = {}
    for (const c of clarifications) {
      if (skip.has(c.key)) continue // omit skipped → backend sources/blank
      resolved[c.key] = resolveAnswer(c)
    }
    if (clarifications.length === 1) {
      const c = clarifications[0]
      const v = resolved[c.key]
      const flat = Array.isArray(v) ? v.join(", ") : v || ""
      onResume({
        action: "answer_question",
        answer: flat,
        answers_recap: recap
      })
    } else {
      onResume({
        action: "answer_question",
        answer: resolved,
        answers_recap: recap
      })
    }
  }

  // --- plan_approval path ---
  if (isApproval) {
    return (
      <PlanApprovalCard
        askUser={askUser}
        submitted={submitted}
        setSubmitted={setSubmitted}
        onResume={onResume}
      />
    )
  }

  // --- submitted: brief "Submitting…" acknowledgement. The "Your answers"
  // recap is rendered in the TIMELINE, not here: the resume handler appends an
  // optimistic `your_answers` step the instant we submit (fast + in place,
  // right where the user answered). This card unmounts the moment the resume
  // handler clears askUser, so this submitting state is only a brief
  // transition. ---
  if (submitted && !isApproval) {
    return (
      <div
        className="p-3 rounded-xl border-[1.5px] border-primary bg-primary/5 flex items-center gap-2"
        data-testid="agent-ask-user-submitting"
      >
        <OSLoader className="xs" />
        <span className="text-13 text-dark-2/70 dark:text-dark-6/70">
          {t("os_agents.label_submitting_answers")}
        </span>
      </div>
    )
  }

  // --- ask_user form path (per-clarification controls or free-text) ---
  const freeTextEmpty = isFreeText && !freeTextAnswer.trim()
  return (
    <div
      className="p-3 rounded-xl border-[1.5px] border-primary bg-primary/5 flex flex-col gap-3"
      data-testid="ask-user-form"
    >
      {askUser.prompt && (
        <p className="text-13 dark:text-dark-6 text-dark-2 whitespace-pre-wrap">
          {askUser.prompt}
        </p>
      )}

      {isFreeText && (
        <textarea
          data-testid="ask-user-input"
          value={freeTextAnswer}
          onChange={(e) => setFreeTextAnswer(e.target.value)}
          disabled={submitted}
          rows={3}
          placeholder={t("os_agents.label_type_your_answer")}
          className="w-full rounded-md border border-light-border dark:border-border bg-white dark:bg-dark-1 px-3 py-2 text-13 text-dark-2 dark:text-dark-6 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
        />
      )}

      {/* Multi-question flow renders ONE clarification at a time with a
          progress chip and Back/Next nav. Single-clarification flows
          (and free-text) fall through to the single-render path below. */}
      {isMultiQ && currentClarif && (
        <>
          <div className="flex items-center justify-between">
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-md text-11 font-medium bg-primary/10 text-primary"
              data-testid="agent-ask-user-progress"
            >
              {currentIdx + 1} / {clarifications.length}
            </span>
          </div>
          {/* key on currentIdx so the slide-in animation re-fires per
              question — uses tailwind.config's pre-defined slide-in-right
              keyframe (translateX 100% → 0 + opacity fade). */}
          <div key={currentIdx} className="animate-slide-in-right">
            <ClarificationControl
              clarification={currentClarif}
              effectiveOptions={effectiveOptionsByKey[currentClarif.key] || []}
              value={answers[currentClarif.key]}
              onChange={(v) => setAnswer(currentClarif.key, v)}
              otherOption={otherOptionByKey[currentClarif.key]}
              otherText={otherTexts[currentClarif.key] || ""}
              onOtherTextChange={(t) => setOtherText(currentClarif.key, t)}
              disabled={submitted}
            />
          </div>
        </>
      )}
      {!isMultiQ && clarifications.length === 1 && (
        <ClarificationControl
          clarification={clarifications[0]}
          effectiveOptions={effectiveOptionsByKey[clarifications[0].key] || []}
          value={answers[clarifications[0].key]}
          onChange={(v) => setAnswer(clarifications[0].key, v)}
          otherOption={otherOptionByKey[clarifications[0].key]}
          otherText={otherTexts[clarifications[0].key] || ""}
          onOtherTextChange={(t) => setOtherText(clarifications[0].key, t)}
          disabled={submitted}
        />
      )}

      {/* flex-wrap lets the buttons stack vertically when there isn't
          enough horizontal room (e.g. PMB v2 workbench's 25% chat column);
          whitespace-nowrap keeps each button's label on a single line so
          they don't grow taller via internal text wrap.

          Multi-Q flows show Back (when not on Q1) + Next (when not on
          last Q) + Submit (on last Q). The cursor-scoped currentMissing
          gates Next; the cross-form missingKey gates Submit as a safety
          net against back-navigation that emptied a prior answer. */}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        {isMultiQ && currentIdx > 0 && !submitted && (
          <button
            type="button"
            onClick={goBack}
            className="px-3 py-1.5 rounded-md border border-light-border dark:border-border text-dark-2 dark:text-dark-6 text-xs hover:bg-primary/5 whitespace-nowrap"
            data-testid="agent-ask-user-back"
          >
            ← {t("os_agents.cta_back") || "Back"}
          </button>
        )}
        {isMultiQ && !isLastQ ? (
          <button
            type="button"
            disabled={submitted || currentMissing != null}
            title={
              currentMissing
                ? t("os_agents.label_please_answer_field", {
                    field: currentClarif?.question || currentMissing
                  })
                : undefined
            }
            onClick={goNext}
            className="px-3 py-1.5 rounded-md bg-primary text-white text-xs hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
            data-testid="agent-ask-user-next"
          >
            {t("os_agents.cta_next")} →
          </button>
        ) : (
          <button
            type="button"
            disabled={
              submitted ||
              (isMultiQ ? missingKey != null : currentMissing != null) ||
              freeTextEmpty
            }
            title={
              (isMultiQ ? missingKey : currentMissing)
                ? t("os_agents.label_please_answer_field", {
                    field:
                      clarifications.find(
                        (c) =>
                          c.key === (isMultiQ ? missingKey : currentMissing)
                      )?.question ||
                      (isMultiQ ? missingKey : currentMissing) ||
                      ""
                  })
                : undefined
            }
            onClick={() => submit()}
            data-testid="ask-user-submit"
            className="px-3 py-1.5 rounded-md bg-primary text-white text-xs hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
          >
            {submitted
              ? t("os_agents.label_submitting", { defaultValue: "Submitting…" })
              : t("os_agents.cta_submit", { defaultValue: "Submit" })}
          </button>
        )}
        {/* Skip — omit the current/last question from the answers. Hidden for
            PMB v2 pickers (they have their own Skip pill below). Right-aligned
            via ml-auto, matching the design. */}
        {!submitted && !_isPmbV2Picker(clarifications) && (
          <button
            type="button"
            onClick={skipCurrent}
            className="ml-auto px-3 py-1.5 rounded-md text-xs text-dark-2/70 dark:text-dark-6/70 hover:text-dark-2 dark:hover:text-dark-6 hover:bg-primary/5 whitespace-nowrap"
            data-testid="agent-ask-user-skip"
          >
            {t("os_agents.cta_skip")}
          </button>
        )}
        {/* PMB v2 Skip pill — only shown when ALL clarifications are PMB-namespaced.
            Resumes the planner with plan_decision.answer.mode="default", which
            the worker mirrors into state.pmb_v2_personalization. */}
        {!submitted && _isPmbV2Picker(clarifications) && (
          <button
            type="button"
            onClick={() => {
              setSubmitted(true)
              onResume({
                action: "answer_question",
                answer: { mode: "default" } as unknown as Record<
                  string,
                  string | string[]
                >
              })
            }}
            className="px-3 py-1.5 rounded-md bg-primary text-white text-xs hover:opacity-90 disabled:opacity-50 whitespace-nowrap"
            data-testid="pmb-v2-skip-pill"
          >
            Generate Default Brief
          </button>
        )}
      </div>
    </div>
  )
}

/** True when this ask_user belongs to the PMB v2 picker.
 *
 * Backend changed shape on 2026-05-19 — Q2 was dropped (see
 * py-processing's pmb_v2/PMB_FLOW.md). Picker now emits pmb_q1 (+ optional
 * pmb_q3). Match on any `pmb_*`-namespaced key so this stays robust to
 * further question reshuffles without surfacing the Default Brief pill on
 * unrelated ask_user turns. */
function _isPmbV2Picker(clarifications: IClarification[]): boolean {
  if (!clarifications || clarifications.length === 0) return false
  return clarifications.some((c) => c.key?.startsWith("pmb_"))
}

/**
 * Plan approval card — three actions:
 *   - Approve & run  → forwards as {action: "approve"}
 *   - Modify with feedback → reveals a textarea; Submit forwards as
 *     {action: "modify", feedback: "<text>"}. The planner re-runs with
 *     the feedback and either revises the plan or (if the feedback
 *     reads as positive) keeps it unchanged — in which case the user
 *     just sees the same approval card again and can click Approve.
 *   - Cancel → forwards as {action: "cancel"}
 *
 * The "Modify" mode is local to this card (doesn't fire onResume on
 * click — only after Submit). Approve / Cancel fire immediately.
 */
function PlanApprovalCard({
  askUser,
  submitted,
  setSubmitted,
  onResume
}: {
  askUser: IAskUser
  submitted: boolean
  setSubmitted: (b: boolean) => void
  onResume: (decision: IResumeDecision) => void
}) {
  const { t } = useTranslation()
  const planSteps = (askUser.plan?.steps || []) as Array<{
    id: string
    title?: string
    description?: string
  }>
  const [mode, setMode] = useState<"idle" | "modify">("idle")
  const [feedback, setFeedback] = useState("")

  const approveOpt = (askUser.options || []).find((o) => o.value === "approve")
  const cancelOpt = (askUser.options || []).find((o) => o.value === "cancel")

  const submitApprove = () => {
    if (submitted) return
    setSubmitted(true)
    onResume({ action: "approve" })
  }
  const submitCancel = () => {
    if (submitted) return
    setSubmitted(true)
    onResume({ action: "cancel" })
  }
  const submitModify = () => {
    if (submitted) return
    const trimmed = feedback.trim()
    if (!trimmed) return // guard: Modify Submit disabled while empty
    setSubmitted(true)
    onResume({ action: "modify", answer: trimmed } as IResumeDecision)
  }

  return (
    <div
      className="p-3 rounded-lg border border-primary/40 bg-primary/5 flex flex-col gap-2"
      data-testid="plan-approval-card"
    >
      <p className="text-13 dark:text-dark-6 text-dark-2">{askUser.prompt}</p>
      {planSteps.length > 0 && (
        <div className="text-[12px] text-dark-2 dark:text-dark-6">
          <div className="font-medium mb-1">
            {t("os_agents.label_proposed_steps")}
          </div>
          <ol className="list-decimal pl-5 space-y-1">
            {planSteps.map((s) => (
              <li key={s.id}>
                <span className="font-medium">{s.title || s.id}</span>
                {s.description && (
                  <div className="text-[11px] text-dark-3 dark:text-dark-5">
                    {s.description}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {mode === "idle" && (
        <div className="flex flex-wrap gap-2">
          {approveOpt && (
            <button
              type="button"
              data-testid="plan-approve-button"
              disabled={submitted}
              className="px-3 py-1.5 rounded-md bg-primary text-white text-xs hover:opacity-90 disabled:opacity-50"
              onClick={submitApprove}
            >
              {approveOpt.label}
            </button>
          )}
          <button
            type="button"
            data-testid="plan-modify-button"
            disabled={submitted}
            className="px-3 py-1.5 rounded-md bg-white dark:bg-dark-2 text-primary text-xs border border-primary/60 hover:bg-primary/5 disabled:opacity-50"
            onClick={() => setMode("modify")}
          >
            {t("os_agents.cta_modify_with_feedback")}
          </button>
          {cancelOpt && (
            <button
              type="button"
              data-testid="plan-cancel-button"
              disabled={submitted}
              className="px-3 py-1.5 rounded-md bg-white dark:bg-dark-2 text-dark-2 dark:text-dark-6 text-xs border border-light-border dark:border-border hover:bg-gray-100 dark:hover:bg-dark-3 disabled:opacity-50"
              onClick={submitCancel}
            >
              {cancelOpt.label}
            </button>
          )}
        </div>
      )}

      {mode === "modify" && (
        <div className="flex flex-col gap-2">
          <label className="text-[12px] font-medium text-dark-2 dark:text-dark-6">
            {t("os_agents.label_what_change")}
          </label>
          <textarea
            disabled={submitted}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={t("os_agents.placeholder_describe_adjustment")}
            rows={3}
            className="px-2 py-1.5 text-[12px] rounded-md border border-primary/60 bg-white dark:bg-dark-2 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-primary resize-y min-h-[64px]"
          />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={submitted || !feedback.trim()}
              className="px-3 py-1.5 rounded-md bg-primary text-white text-xs hover:opacity-90 disabled:opacity-50"
              onClick={submitModify}
            >
              {submitted
                ? t("os_agents.label_sending")
                : t("os_agents.cta_send_feedback")}
            </button>
            <button
              type="button"
              disabled={submitted}
              className="px-3 py-1.5 rounded-md bg-white dark:bg-dark-2 text-dark-2 dark:text-dark-6 text-xs border border-light-border dark:border-border hover:bg-gray-100 dark:hover:bg-dark-3 disabled:opacity-50"
              onClick={() => {
                setMode("idle")
                setFeedback("")
              }}
            >
              {t("os_agents.cta_back")}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Searchable multi-select picker for long option lists (e.g. an entire
 * 222-account authorized list). Shows a search input + scroll panel of
 * checkboxes; selected items also pinned as removable chips at the top
 * for visibility regardless of search filter.
 *
 * Why not antd's Select? Keeps the dependency surface narrow and makes
 * the styling match the rest of the inline chip vocabulary.
 */
function MultiSelectPicker({
  options,
  value,
  onChange,
  disabled,
  otherOption
}: {
  options: string[]
  value: string[]
  onChange: (v: string[]) => void
  disabled: boolean
  /**
   * The "Other (please specify)" sentinel, if present in `options`.
   * Excluded from "select all" so a bulk-select never arms the
   * free-text escape hatch.
   */
  otherOption?: string
}) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.toLowerCase().includes(q))
  }, [options, query])

  const selectable = useMemo(
    () => options.filter((o) => o !== otherOption),
    [options, otherOption]
  )

  const toggle = (opt: string) => {
    if (disabled) return
    const next = value.includes(opt)
      ? value.filter((x) => x !== opt)
      : [...value, opt]
    onChange(next)
  }

  // "Select all" is rendered as the first option-row (not a separate
  // toolbar). It toggles every selectable value at once and never touches
  // the "Other (please specify)" sentinel.
  const allSelected =
    selectable.length > 0 && selectable.every((o) => value.includes(o))
  const toggleSelectAll = () => {
    if (disabled) return
    onChange(
      allSelected
        ? value.filter((v) => !selectable.includes(v))
        : Array.from(new Set([...value, ...selectable]))
    )
  }

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-light-border dark:border-border bg-white dark:bg-dark-2 p-2">
      {/* Search */}
      <input
        type="text"
        disabled={disabled}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("os_agents.placeholder_search_options", {
          count: options.length
        })}
        className="px-2 py-1 text-[12px] rounded-md border border-light-border dark:border-border bg-white dark:bg-dark-2 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-primary"
      />

      {/* Scrollable option list. Capped height so the form stays
          usable; user filters via search. */}
      <div className="max-h-48 overflow-auto flex flex-col">
        {/* "Select all" as the first option-row — only in the unfiltered
            view (when searching, the user is narrowing to specific items).
            Styled identically to an option below; excludes the "Other"
            sentinel. */}
        {!query.trim() && selectable.length > 0 && (
          <label
            className={`cursor-pointer flex items-center gap-2 px-1.5 py-1 rounded text-[12px] hover:bg-primary/5 ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <input
              type="checkbox"
              data-testid="ask-user-select-all"
              disabled={disabled}
              checked={allSelected}
              onChange={toggleSelectAll}
              className="accent-primary"
            />
            <span className="text-dark-2 dark:text-dark-6">
              {t("os_agents.cta_select_all", { count: selectable.length })}
            </span>
          </label>
        )}
        {filtered.length === 0 ? (
          <p className="text-[11px] text-dark-3 dark:text-dark-5 px-1 py-2">
            {t("os_agents.label_no_matches")}
          </p>
        ) : (
          filtered.map((opt) => {
            const checked = value.includes(opt)
            return (
              <label
                key={opt}
                className={`cursor-pointer flex items-center gap-2 px-1.5 py-1 rounded text-[12px] hover:bg-primary/5 ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => toggle(opt)}
                  className="accent-primary"
                />
                <span className="text-dark-2 dark:text-dark-6">{opt}</span>
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}

// Single-select sibling of MultiSelectPicker — same bordered-box UX, but
// radios instead of checkboxes and a single string value. Renders EVERY
// single_choice / format_preference clarification (never pills), so the
// single-select UX is consistent everywhere. The search box only appears for
// long lists (> 8 options); short lists are just clean radio rows.
function SingleSelectPicker({
  options,
  value,
  onChange,
  disabled
}: {
  options: string[]
  value: string | undefined
  onChange: (v: string) => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter((o) => o.toLowerCase().includes(q))
  }, [options, query])

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-light-border dark:border-border bg-white dark:bg-dark-2 p-2">
      {/* Search — only for long lists; short lists are clean radio rows */}
      {options.length > 8 && (
        <input
          type="text"
          disabled={disabled}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("os_agents.placeholder_search_options", {
            count: options.length
          })}
          className="px-2 py-1 text-[12px] rounded-md border border-light-border dark:border-border bg-white dark:bg-dark-2 disabled:opacity-50 focus:outline-none focus:ring-1 focus:ring-primary"
        />
      )}

      {/* Scrollable single-select list */}
      <div className="max-h-48 overflow-auto flex flex-col">
        {filtered.length === 0 ? (
          <p className="text-[11px] text-dark-3 dark:text-dark-5 px-1 py-2">
            {t("os_agents.label_no_matches")}
          </p>
        ) : (
          filtered.map((opt) => {
            const checked = value === opt
            return (
              <label
                key={opt}
                className={`cursor-pointer flex items-center gap-2 px-1.5 py-1 rounded text-[12px] hover:bg-primary/5 ${
                  checked ? "bg-primary/10" : ""
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <input
                  type="radio"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => !disabled && onChange(opt)}
                  className="accent-primary"
                />
                <span className="text-dark-2 dark:text-dark-6">{opt}</span>
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}

function ClarificationControl({
  clarification,
  effectiveOptions,
  value,
  onChange,
  otherOption,
  otherText,
  onOtherTextChange,
  disabled
}: {
  clarification: IClarification
  /**
   * Resolved option list — for normal clarifications equal to
   * `clarification.options`, but for `options_source`-marked ones the
   * parent supplies the live-fetched data (e.g. user authorized
   * accounts via useGetAccounts). Centralising this in the parent
   * keeps the picker, "Other"-detection, and resolveAnswer logic in
   * sync.
   */
  effectiveOptions: string[]
  value: string | string[] | undefined
  onChange: (v: string | string[]) => void
  /** The option string that represents "Other (specify)", if any. */
  otherOption?: string
  /** Current typed value for the other-text input. */
  otherText: string
  onOtherTextChange: (v: string) => void
  disabled: boolean
}) {
  const { t } = useTranslation()
  const c = clarification
  const hint = c.context

  // True while the parent's live-fetch hasn't returned anything yet —
  // shows a "Loading your accounts…" placeholder so the user knows the
  // chips will populate. (Heuristic: the marker is set but
  // effectiveOptions only carries the trailing "Other (please specify)"
  // sentinel and nothing else.)
  const isAccountsPickerLoading =
    c.options_source === "user_authorized_accounts" &&
    effectiveOptions.length <= 1

  // Is the "Other" sentinel currently selected? Drives the reveal of the
  // free-text input under the chips.
  const otherSelected = otherOption
    ? Array.isArray(value)
      ? value.includes(otherOption)
      : value === otherOption
    : false

  // Person-disambiguation escape ("None of these") must be a valid LinkedIn
  // profile URL — flag a non-empty-but-invalid entry so we can show a hint and
  // a red border (the parent's validateOne blocks Submit on the same check).
  const otherUrlInvalid =
    otherSelected &&
    c.options_source === "person_disambiguation" &&
    otherText.trim().length > 0 &&
    !isLinkedInProfileUrl(otherText)

  return (
    <div className="flex flex-col gap-1.5">
      {c.question && (
        // `text-13` is the project's only project-defined text utility
        // (tailwind.config.ts → fontSize."13"). `text-12` does NOT
        // exist in the theme — using it on a <label> produced no CSS
        // rule and the browser default (~16px) kicked in, making the
        // question render visibly larger + heavier than the surrounding
        // askUser.prompt and context strings (RCA: user feedback "text
        // size + color inconsistent on ask_user form"). text-13
        // matches the prompt above and the textarea below; font-medium
        // keeps the label slightly heavier than body text so it still
        // reads as a label, not a paragraph.
        <label className="text-13 font-medium text-dark-2 dark:text-dark-6">
          {c.question}
        </label>
      )}
      {hint && (
        <p className="text-[11px] text-dark-3 dark:text-dark-5">{hint}</p>
      )}

      {isAccountsPickerLoading && (
        <div className="flex items-center gap-2 text-[11px] text-dark-3 dark:text-dark-5">
          <OSLoader className="xs" />
          <span>{t("os_agents.label_loading_accounts")}</span>
        </div>
      )}

      {c.answer_type === "multi_choice" && (
        // Always use the searchable multi-select picker (search +
        // scrollable checkbox list) — one consistent UI regardless of
        // option count, rather than a separate chip layout for short
        // lists.
        <MultiSelectPicker
          options={effectiveOptions}
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          disabled={disabled}
          otherOption={otherOption}
        />
      )}

      {(c.answer_type === "single_choice" ||
        c.answer_type === "format_preference") && (
        // Single-choice always renders as a radio-row picker
        // (SingleSelectPicker), never pills — one consistent single-select
        // UX across every clarification. The picker shows a search box only
        // for long lists; short lists are clean radio rows.
        <SingleSelectPicker
          options={effectiveOptions}
          value={typeof value === "string" ? value : undefined}
          onChange={onChange}
          disabled={disabled}
        />
      )}

      {/* Free-text input revealed when the "Other (specify)" sentinel is
          picked. For multi_choice the user may type several names
          comma-separated — the parent splits on commas at submit time
          and merges with the other checkbox picks (removing the
          sentinel). For single_choice the typed text REPLACES the
          sentinel entirely. */}
      {otherOption && otherSelected && (
        <input
          type="text"
          disabled={disabled}
          value={otherText}
          onChange={(e) => onOtherTextChange(e.target.value)}
          placeholder={
            c.options_source === "person_disambiguation"
              ? t("os_agents.placeholder_paste_linkedin_url")
              : c.answer_type === "multi_choice"
                ? t("os_agents.placeholder_type_names_csv")
                : t("os_agents.placeholder_type_your_answer")
          }
          className={`px-2 py-1 text-[12px] rounded-md border bg-white dark:bg-dark-2 disabled:opacity-50 focus:outline-none focus:ring-1 ${
            otherUrlInvalid
              ? "border-red-500 focus:ring-red-500"
              : "border-primary/60 focus:ring-primary"
          }`}
        />
      )}
      {otherUrlInvalid && (
        <p className="text-[11px] text-red-500">
          {t("os_agents.error_invalid_linkedin_url")}
        </p>
      )}

      {c.answer_type === "number" && (
        <input
          type="number"
          inputMode="numeric"
          disabled={disabled}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="px-2 py-1 text-[12px] rounded-md border border-light-border dark:border-border bg-white dark:bg-dark-2 disabled:opacity-50"
        />
      )}

      {c.answer_type === "text" && (
        <input
          type="text"
          disabled={disabled}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="px-2 py-1 text-[12px] rounded-md border border-light-border dark:border-border bg-white dark:bg-dark-2 disabled:opacity-50"
        />
      )}
    </div>
  )
}
